import dot from 'dot-prop-immutable';
import { denormalize } from 'normalizr';
import { sort } from 'ramda';
import reduceReducers from 'reduce-reducers';

import { createEntitiesReducer } from 'modules/common/redux';
import { FORMULA_COMPILE_SUCCESS_MSG } from 'modules/constants/common/all';

import { formatTimestamp } from 'utils/dateUtils';
import { createAlphabeticalComparator } from 'utils/helpers';
import { getErrorMessage } from 'utils/stringUtils';

import { types } from './actions';
import * as entities from './entities';

const initialState = {
  entities: {},
  opDef: null,
  opDefs: null,
  processClassIds: [],
  search: {
    isFetching: false,
    query: '',
    result: null,
  },
  formula: {
    error: false,
    isCompiling: false,
    isFetching: false,
    message: null,
  },
  isFetchingManufacturers: false,
  isFetchingMachines: false,
};

const entitiesReducer = createEntitiesReducer(
  [
    'machines',
    'manufacturers',
    'opDefs',
    'processClasses',
    'processClassManufacturerMachines',
    'processClassManufacturers',
    'variables',
  ],
  [
    types.ADD_OPDEFS_TO_STORE,
    types.FETCH_MACHINES_SUCCESS,
    types.FETCH_MANUFACTURERS_SUCCESS,
    types.RECEIVE_OPDEF,
    types.RECEIVE_PROCESS_CLASSES,
    types.SEARCH_OP_DEFS_SUCCESS,
    types.UPDATE_FORMULA_SUCCESS,
    types.UPDATE_OPDEF_SUCCESS,
    types.UPDATE_VARIABLE_SUCCESS,
  ],
);

const reducer = (state, action) => {
  switch (action.type) {
    case types.RECEIVE_OPDEF:
      return { ...state, opDef: action.payload.result };
    case types.RECEIVE_OPDEFS:
      return { ...state, opDefs: action.payload };
    case types.FETCH_MANUFACTURERS_REQUEST:
      return {
        ...state,
        isFetchingManufacturers: true,
      };
    case types.FETCH_MANUFACTURERS_SUCCESS:
    case types.FETCH_MANUFACTURERS_ERROR:
      return {
        ...state,
        isFetchingManufacturers: false,
      };
    case types.FETCH_MACHINES_REQUEST:
      return {
        ...state,
        isFetchingMachines: true,
      };
    case types.FETCH_MACHINES_SUCCESS:
    case types.FETCH_MACHINES_ERROR:
      return {
        ...state,
        isFetchingMachines: false,
      };
    case types.RECEIVE_PROCESS_CLASSES:
      return {
        ...state,
        processClassIds: action.payload.result,
      };
    case types.UPDATE_FORMULA_REQUEST:
      return dot.merge(state, 'formula', {
        isFetching: true,
      });
    case types.UPDATE_FORMULA_SUCCESS: {
      let nextState = state;
      nextState = dot.merge(
        nextState,
        `entities.opDefs.${action.payload.opDefId}.formula`,
        action.payload.formula,
      );
      const savedMsg = action.payload.formula.revertedFrom
        ? `Restored from: ${formatTimestamp(
            action.payload.formula.revertedFrom,
          )}`
        : 'Saved';
      nextState = dot.merge(nextState, 'formula', {
        isFetching: false,
        error: false,
        message: savedMsg,
      });
      return nextState;
    }
    case types.UPDATE_FORMULA_ERROR:
      return dot.merge(state, 'formula', {
        isFetching: false,
        error: true,
        message: getErrorMessage(action.payload),
      });
    case types.COMPILE_FORMULA_REQUEST:
      return dot.set(state, 'formula.isCompiling', true);
    case types.COMPILE_FORMULA_SUCCESS:
      return dot.merge(state, 'formula', {
        isCompiling: false,
        error: false,
        message: FORMULA_COMPILE_SUCCESS_MSG,
      });
    case types.COMPILE_FORMULA_ERROR:
      return dot.merge(state, 'formula', {
        isCompiling: false,
        error: true,
        message: getErrorMessage(action.payload),
      });
    case types.RESET_FORMULA_MESSAGE:
      return dot.merge(state, 'formula', {
        error: false,
        message: null,
      });
    case types.SEARCH_OP_DEFS_REQUEST:
      return dot.merge(state, 'search', {
        isFetching: true,
        query: action.payload.query,
      });
    case types.SEARCH_OP_DEFS_SUCCESS:
      return dot.merge(state, 'search', {
        isFetching: false,
        result: action.payload.result,
      });
    case types.SEARCH_OP_DEFS_ERROR:
      return dot.merge(state, 'search', {
        isFetching: false,
      });
    default:
      return state;
  }
};

export default reduceReducers(entitiesReducer, reducer, initialState);

export const getScope = (state) => state.modules.opDefs.data;

export const getOpDef = (state) => {
  return denormalize(
    getScope(state).opDef,
    entities.opDefSchema,
    getScope(state).entities,
  );
};

export const getOpDefsByIds = (state, opDefIds) =>
  denormalize(opDefIds, [entities.opDefSchema], getScope(state).entities);

export const getOpDefs = (state, { sortByName = false } = {}) => {
  let result = getOpDefsByIds(state, getScope(state).opDefs);

  if (sortByName) {
    result = sort(createAlphabeticalComparator('name'), result);
  }
  return result;
};

export const getAllOpDefsInStore = (state) => getScope(state).entities.opDefs;

export const getOpDefById = (state, opDefId) =>
  denormalize(opDefId, entities.opDefSchema, getScope(state).entities);

export const getManufacturers = (state, processClassId) => {
  const data = denormalize(
    processClassId,
    entities.processClassManufacturerSchema,
    getScope(state).entities,
  );
  return data && data.manufacturers;
};

export const getMachines = (state, processClassId, manufacturerId) => {
  const data = denormalize(
    `${processClassId}_${manufacturerId}`,
    entities.processClassManufacturerMachineSchema,
    getScope(state).entities,
  );
  return data && data.machines;
};

export const getProcessClasses = (state) =>
  denormalize(
    getScope(state).processClassIds,
    [entities.processClassSchema],
    getScope(state).entities,
  );

export const getFormulaState = (state) => getScope(state).formula;

export const getFoundOpDefs = (state) => {
  const ids = getScope(state).search.result;
  return ids && getOpDefsByIds(state, ids);
};

export const getIsSearching = (state) => getScope(state).search.isFetching;

export const isFetchingMachines = (state) => getScope(state).isFetchingMachines;

export const isFetchingManufacturers = (state) =>
  getScope(state).isFetchingManufacturers;
